<template>
  <div class="ad-popup-overlay" @click="handleClose">
    <div class="ad-popup-container" @click.stop>
      <div class="ad-popup-header">
        <span class="ad-title">广告</span>
        <div class="ad-close-area">
          <span class="countdown">{{ countdown }}秒后关闭</span>
          <button class="close-btn" @click="handleClose">×</button>
        </div>
      </div>
      
      <div 
        class="ad-carousel" 
        @mouseenter="pauseAutoplay"
        @mouseleave="resumeAutoplay"
      >
        <div class="carousel-wrapper">
          <div 
            v-for="(image, index) in adImages" 
            :key="index"
            class="carousel-slide"
            :class="{ active: currentIndex === index }"
          >
            <img :src="image" :alt="`广告图片${index + 1}`" />
          </div>
        </div>
        
        <div class="carousel-indicators">
          <span 
            v-for="(_, index) in adImages" 
            :key="index"
            class="indicator"
            :class="{ active: currentIndex === index }"
            @click="goToSlide(index)"
          ></span>
        </div>
        
        <button class="carousel-btn prev" @click="prevSlide">‹</button>
        <button class="carousel-btn next" @click="nextSlide">›</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';

import banner1 from '@/assets/banners/1.png';
import banner2 from '@/assets/banners/2.png';
import banner3 from '@/assets/banners/3.png';

export default {
  name: 'AdPopup',
  emits: ['close'],
  setup(props, { emit }) {
    const adImages = [banner1, banner2, banner3];
    const currentIndex = ref(0);
    const countdown = ref(15);
    const isPaused = ref(false);
    
    let autoplayTimer = null;
    let countdownTimer = null;

    const startAutoplay = () => {
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        if (!isPaused.value) {
          nextSlide();
        }
      }, 2000);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const pauseAutoplay = () => {
      isPaused.value = true;
    };

    const resumeAutoplay = () => {
      isPaused.value = false;
    };

    const nextSlide = () => {
      currentIndex.value = (currentIndex.value + 1) % adImages.length;
    };

    const prevSlide = () => {
      currentIndex.value = (currentIndex.value - 1 + adImages.length) % adImages.length;
    };

    const goToSlide = (index) => {
      currentIndex.value = index;
    };

    const startCountdown = () => {
      countdownTimer = setInterval(() => {
        if (!isPaused.value) {
          countdown.value--;
          if (countdown.value <= 0) {
            handleClose();
          }
        }
      }, 1000);
    };

    const stopCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    };

    const handleClose = () => {
      stopAutoplay();
      stopCountdown();
      emit('close');
    };

    onMounted(() => {
      startAutoplay();
      startCountdown();
    });

    onUnmounted(() => {
      stopAutoplay();
      stopCountdown();
    });

    return {
      adImages,
      currentIndex,
      countdown,
      isPaused,
      nextSlide,
      prevSlide,
      goToSlide,
      pauseAutoplay,
      resumeAutoplay,
      handleClose
    };
  }
};
</script>

<style scoped>
.ad-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.ad-popup-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  width: 70vw;
  height: 60vh;
  animation: popupFadeIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

@keyframes popupFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.ad-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.ad-title {
  font-size: 16px;
  font-weight: bold;
}

.ad-close-area {
  display: flex;
  align-items: center;
  gap: 12px;
}

.countdown {
  font-size: 13px;
  opacity: 0.9;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  font-size: 24px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  line-height: 1;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.ad-carousel {
  position: relative;
  width: 100%;
  flex: 1;
  overflow: hidden;
}

.carousel-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.carousel-slide.active {
  opacity: 1;
}

.carousel-slide img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
}

.carousel-indicators {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.indicator.active {
  background-color: #fff;
  transform: scale(1.2);
}

.indicator:hover {
  background-color: rgba(255, 255, 255, 0.8);
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.4);
  color: #fff;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  transition: background-color 0.3s;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-btn:hover {
  background-color: rgba(0, 0, 0, 0.6);
}

.carousel-btn.prev {
  left: 15px;
}

.carousel-btn.next {
  right: 15px;
}
</style>
